import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { iqQuestions } from '@/mocks/iqTestData';
import IqIntro from './components/IqIntro';
import IqQuestion from './components/IqQuestion';
import IqResult from './components/IqResult';
import TestResumeBanner from '@/components/feature/TestResumeBanner';
import { TEST_PROGRESS_KEYS, readProgress, writeProgress, clearProgress } from '@/lib/testProgress';

export type IqStep = 'intro' | 'test' | 'result';

const TIME_LIMIT = 30 * 60; // 30분 (1800초)

interface IqProgress {
  current: number;
  answers: Record<number, number>;
  timeLeft: number;
}

function formatTime(totalSeconds: number) {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export default function IqTestPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<IqStep>('intro');
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [timeLeft, setTimeLeft] = useState(TIME_LIMIT);
  const [resumeDetail, setResumeDetail] = useState<string | null>(null);

  // 마운트 시 저장된 진행 상황이 있는지 확인
  useEffect(() => {
    const saved = readProgress<IqProgress>(TEST_PROGRESS_KEYS.iq);
    if (saved && (Object.keys(saved.answers || {}).length > 0 || saved.current > 0)) {
      const done = Math.min(saved.current + 1, iqQuestions.length);
      setResumeDetail(`종합 인지능력(IQ) 검사 ${done} / ${iqQuestions.length}문항 진행 중`);
    }
  }, []);

  // 검사 진행 상황을 자동 저장
  useEffect(() => {
    if (step !== 'test') return;
    if (Object.keys(answers).length === 0 && current === 0) return;
    writeProgress(TEST_PROGRESS_KEYS.iq, { current, answers, timeLeft });
  }, [step, current, answers, timeLeft]);

  useEffect(() => {
    if (step !== 'test') return;
    if (timeLeft <= 0) {
      setStep('result');
      return;
    }
    const timer = setTimeout(() => setTimeLeft((t) => t - 1), 1000);
    return () => clearTimeout(timer);
  }, [step, timeLeft]);

  const clearAll = () => {
    clearProgress(TEST_PROGRESS_KEYS.iq);
    setResumeDetail(null);
  };

  const resume = () => {
    const saved = readProgress<IqProgress>(TEST_PROGRESS_KEYS.iq);
    if (saved) {
      setCurrent(saved.current || 0);
      setAnswers(saved.answers || {});
      setTimeLeft(saved.timeLeft > 0 ? saved.timeLeft : TIME_LIMIT);
      setStep('test');
    }
  };

  const startTest = () => {
    clearAll();
    setAnswers({});
    setCurrent(0);
    setTimeLeft(TIME_LIMIT);
    setStep('test');
  };

  const retake = () => {
    clearAll();
    setAnswers({});
    setCurrent(0);
    setTimeLeft(TIME_LIMIT);
    setStep('intro');
  };

  // 결과 도달 시 저장된 진행 상황 정리
  useEffect(() => {
    if (step === 'result') clearAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const isLowTime = timeLeft <= 300;

  return (
    <div className="min-h-screen bg-background-50">
      {/* Top Nav */}
      <nav className="bg-background-50 border-b border-background-200 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate('/')}
            className="w-8 h-8 flex items-center justify-center text-foreground-600 hover:text-foreground-950 cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
          </button>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 flex items-center justify-center bg-primary-500 rounded-lg">
              <i className="ri-psychotherapy-line text-white text-xs"></i>
            </div>
            <span className="font-semibold text-foreground-950 text-sm">종합 인지능력(IQ) 검사</span>
          </div>

          {step === 'test' && (
            <div className="ml-auto flex items-center gap-3">
              <div
                className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${
                  isLowTime ? 'bg-accent-100 text-accent-700' : 'bg-background-100 text-foreground-600'
                }`}
              >
                <i className="ri-time-line"></i>
                {formatTime(timeLeft)}
              </div>
              <span className="text-xs text-foreground-500">{current + 1} / {iqQuestions.length}</span>
            </div>
          )}
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-6 md:py-10">
        {step === 'intro' && (
          <>
            {resumeDetail && (
              <div className="max-w-2xl mx-auto mb-6">
                <TestResumeBanner
                  detail={resumeDetail}
                  onResume={resume}
                  onReset={clearAll}
                />
              </div>
            )}
            <IqIntro onStart={startTest} />
          </>
        )}
        {step === 'test' && (
          <IqQuestion
            current={current}
            answers={answers}
            onSelect={(idx) => {
              setAnswers((prev) => ({ ...prev, [iqQuestions[current].id]: idx }));
            }}
            onNext={() => setCurrent((c) => c + 1)}
            onPrev={() => setCurrent((c) => Math.max(0, c - 1))}
            onSubmit={() => setStep('result')}
          />
        )}
        {step === 'result' && (
          <IqResult
            answers={answers}
            onRetake={retake}
          />
        )}
      </main>
    </div>
  );
}